import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_ACKNOWLEDGED_BY_USER } from "my-time/graphql/mutations";
import { GET_APPROVAL_STATE } from "my-time/graphql/queries/get-approval-state";
import { useMyTimeUserApprovalState } from "my-time/hooks/use-user-approval-state";
import { myTimeAcknowledgedState } from "my-time/states/is-acknowledged-state";
import { useTranslation } from "react-i18next";
import { useOcSnackbar } from "oc/hooks";

export const useMyTimeUserApproval = () => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();
  const hasSetAcknowledgedRef = useRef(false);

  const { state: userApprovalEnabled } = useMyTimeUserApprovalState();
  const [isAcknowledged, setIsAcknowledged] = useRecoilState(
    myTimeAcknowledgedState,
  );

  const {
    data: userApprovalState,
    loading,
    error,
    refetch,
  } = useQuery(GET_APPROVAL_STATE, {
    fetchPolicy: "network-only",
  });

  // Handle data initialization via useEffect
  useEffect(() => {
    if (
      userApprovalState?.getApprovalState &&
      !loading &&
      !hasSetAcknowledgedRef.current
    ) {
      hasSetAcknowledgedRef.current = true;
      setIsAcknowledged(userApprovalState.getApprovalState.isAcknowledged);
    }
  }, [userApprovalState, loading, setIsAcknowledged]);

  // Handle error via useEffect
  useEffect(() => {
    if (error) {
      console.error("Error fetching approval state:", error);
    }
  }, [error]);

  const [createAcknowledgedByUserMutation, { loading: createAcknowledgedByUserLoading }] =
    useMutation(CREATE_ACKNOWLEDGED_BY_USER);

  const createAcknowledgedByUser = useCallback(
    async (options?: any) => {
      try {
        const result = await createAcknowledgedByUserMutation(options);
        setIsAcknowledged(true);
        refetch();
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_USER_APPROVAL_SUCCESS"),
        });
        return result;
      } catch (error) {
        console.error("Error acknowledging user:", error);
        setOpenMessage({
          type: "error",
          message: t("MY_TIME_USER_APPROVAL_ERROR"),
        });
        throw error;
      }
    },
    [createAcknowledgedByUserMutation, setIsAcknowledged, refetch, setOpenMessage, t],
  );

  return {
    isApproved: userApprovalState?.getApprovalState?.isApproved || false,
    approvalStateLoading: loading,
    userApprovalEnabled,
    isAcknowledged,
    setIsAcknowledged,
    refetch,
    createAcknowledgedByUser,
    createAcknowledgedByUserLoading,
    loading,
  };
};

export default useMyTimeUserApproval;
